import { useRef, useCallback } from 'react';
import { useComboBox } from '@react-aria/combobox';
import { mergeProps } from '@react-aria/utils';
import { useForwardedRef } from '../../hooks';
import { Paper } from '../paper';
import { useSelectState } from './useSelectState';
import { useTheme } from '../ThemeProvider';
import { useModalFix } from './useModalFix';
export function useSelect(props, ref) {
    const { options, renderOption, onFocus, onBlur, value, children, onChange, disabledKeys, makeKey, multi, portal, size = 'm', itemsVisible = 5, name, animated, makeText, creatable, onInputChange: onInputChangeProp, filter, openOnFocus = true, popperProps, ...rest } = props;
    const targetRef = useForwardedRef(ref);
    const popoverRef = useRef(null);
    const inputRef = useRef(null);
    const listBoxRef = useRef(null);
    const theme = useTheme();
    const state = useSelectState(props);
    const { inputProps, listBoxProps, labelProps } = useComboBox({
        defaultItems: options,
        menuTrigger: 'focus',
        disabledKeys: state.disabledKeys,
        inputRef,
        listBoxRef,
        popoverRef,
        label: props['aria-label'],
    }, state);
    useModalFix({
        state: state,
        popoverRef,
        targetRef,
    });
    const { value: inputValue, onChange: onInputChange, ...restInputProps } = inputProps;
    const onInputChangeHandler = function (value, e) {
        onInputChangeProp === null || onInputChangeProp === void 0 ? void 0 : onInputChangeProp(value);
        onInputChange === null || onInputChange === void 0 ? void 0 : onInputChange(e);
    };
    const onClick = useCallback((e) => {
        var _a;
        if (!openOnFocus) {
            state.open();
        }
        (_a = rest.onClick) === null || _a === void 0 ? void 0 : _a.call(rest, e);
    }, [state, openOnFocus, rest.onClick]);
    const textValue = (Array.isArray(value) ? value : value !== undefined && value !== null ? [value] : [])
        .map(makeText)
        .join(',');
    return {
        textFieldProps: mergeProps(rest, {
            value: inputValue,
            onChange: onInputChangeHandler,
            onClick,
            inputProps: {
                ref: inputRef,
            },
            animated: animated === false ? false : multi ? !Array.isArray(value) || !value.length : true,
            size,
            ref: targetRef,
        }),
        popperProps: {
            ...popperProps,
            target: targetRef,
            opened: state.isOpen,
            // opened: true,
            ref: popoverRef,
            Component: Paper,
            portal,
            dismissable: false,
            flexDirection: 'column',
            sameWidth: true,
            style: {
                maxHeight: theme.components.slots.size[size] * itemsVisible,
                ['--list-border-radius']: 'var(--border-radius)',
            },
        },
        listProps: {
            ...listBoxProps,
            disabledKeys: state.disabledKeys,
            items: options,
            children: renderOption,
            selectionMode: state.selectionMode,
            state,
            size,
            shouldFocusOnHover: false,
        },
        hiddenInputProps: {
            type: 'text',
            tabIndex: -1,
            ...(value !== undefined
                ? {
                    value: textValue,
                    name: name,
                }
                : {
                    disabled: true,
                    readOnly: true,
                }),
        },
        inputProps: restInputProps,
        labelProps,
        state,
    };
}
